/**
 * Google Integration Component
 * Manages Gmail and Google Calendar integration settings
 */

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useSearchParams } from 'react-router-dom'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/contexts/UserContext'
import { apiClient } from '@/lib/api'
import { toDisplayDate } from '@/lib/utils'
import {
  Loader2,
  Mail,
  Calendar,
  RefreshCw,
  Link2,
  Unlink,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import type { IntegrationStatus } from '@/types/integration'
import { CATEGORICAL, SEMANTIC } from '@/lib/theme'

export function GoogleIntegration() {
  const { toast } = useToast()
  const { isSuperAdmin } = useUser()
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncProgress, setSyncProgress] = useState<string | null>(null)
  const [connecting, setConnecting] = useState(false)
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus | null>(null)
  // Super-Admin-only pre-OAuth choices. The two checkboxes let a Super
  // Admin connect Google for tokens-only (e.g., Meet link generation,
  // conflict detection) without ingesting either Gmail messages OR
  // Calendar events into the CRM. Non-Super-Admins never see these
  // checkboxes; their connect always defaults to true for both (legacy
  // behaviour preserved).
  const [includeGmailOnConnect, setIncludeGmailOnConnect] = useState(true)
  const [includeCalendarOnConnect, setIncludeCalendarOnConnect] = useState(true)
  const [syncSettings, setSyncSettings] = useState({
    sync_emails: true,
    sync_calendar: true,
    sync_frequency_minutes: 15,
  })

  const [searchParams, setSearchParams] = useSearchParams()

  // Fetch integration status on mount
  useEffect(() => {
    fetchIntegrationStatus()
  }, [])

  // Surface a rejected Google connect (authorized the wrong account). The backend
  // redirects here with ?error=account_mismatch&expected=&got= and stores nothing.
  useEffect(() => {
    if (searchParams.get('error') === 'account_mismatch') {
      const expected = searchParams.get('expected') || 'your account'
      const got = searchParams.get('got') || 'a different account'
      toast({
        title: 'Wrong Google account',
        description: `You authorized ${got}, but your CRM account is ${expected}. Please connect your own Google account.`,
        variant: 'destructive',
      })
      // Clear the params so the toast doesn't reappear on refresh.
      const next = new URLSearchParams(searchParams)
      next.delete('error')
      next.delete('expected')
      next.delete('got')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const fetchIntegrationStatus = async () => {
    try {
      setLoading(true)
      const status = await apiClient.getIntegrationStatus()
      setIntegrationStatus(status)

      if (status.integration) {
        setSyncSettings({
          sync_emails: status.integration.sync_emails,
          sync_calendar: status.integration.sync_calendar,
          sync_frequency_minutes: status.integration.sync_frequency_minutes,
        })
      }
    } catch (error) {
      console.error('Failed to fetch integration status:', error)
      toast({
        title: 'Error',
        description: 'Failed to fetch integration status',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async () => {
    try {
      setConnecting(true)
      // Only pass params for explicit opt-outs — keeps the backend's
      // "absent means don't touch on reconnect" semantic intact.
      const opts: { syncEmails?: boolean; syncCalendar?: boolean } = {}
      if (isSuperAdmin && !includeGmailOnConnect) opts.syncEmails = false
      if (isSuperAdmin && !includeCalendarOnConnect) opts.syncCalendar = false
      const { authUrl } = await apiClient.getGoogleAuthUrl(
        Object.keys(opts).length > 0 ? opts : undefined
      )

      // Open OAuth consent screen in new window
      const authWindow = window.open(authUrl, 'google-auth', 'width=600,height=600')

      // Poll for window close and refresh status
      const pollTimer = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(pollTimer)
          setConnecting(false)
          fetchIntegrationStatus()
        }
      }, 1000)
    } catch (error) {
      console.error('Failed to get auth URL:', error)
      toast({
        title: 'Error',
        description: 'Failed to initiate Google authentication',
        variant: 'destructive',
      })
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        'Are you sure you want to disconnect your Google account? This will stop syncing emails and calendar events.'
      )
    ) {
      return
    }

    try {
      setLoading(true)
      await apiClient.disconnectGoogleIntegration()

      toast({
        title: 'Success',
        description: 'Google integration disconnected successfully',
      })

      await fetchIntegrationStatus()
    } catch (error) {
      console.error('Failed to disconnect:', error)
      toast({
        title: 'Error',
        description: 'Failed to disconnect Google integration',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async (syncType: 'full' | 'incremental' = 'incremental') => {
    try {
      setSyncing(true)
      const response = await apiClient.triggerGoogleSync(syncType)

      toast({
        title: 'Sync Started',
        description: `${syncType === 'full' ? 'Full' : 'Incremental'} sync initiated. This may take a few minutes.`,
      })

      // Poll for sync completion
      pollSyncStatus(response.sync_id)
    } catch (error) {
      console.error('Failed to trigger sync:', error)
      toast({
        title: 'Error',
        description: 'Failed to start sync',
        variant: 'destructive',
      })
      setSyncing(false)
    }
  }

  const pollSyncStatus = async (syncId: string) => {
    const maxAttempts = 60 // Poll for up to 5 minutes
    let attempts = 0

    const pollInterval = setInterval(async () => {
      attempts++

      try {
        const { sync } = await apiClient.getSyncStatus(syncId)

        if (sync.sync_status === 'completed') {
          clearInterval(pollInterval)
          setSyncing(false)
          setSyncProgress(null)

          toast({
            title: 'Sync Complete',
            description: `Synced ${sync.items_created || 0} new items and updated ${sync.items_updated || 0} items.`,
          })

          await fetchIntegrationStatus()
        } else if (sync.sync_status === 'failed') {
          clearInterval(pollInterval)
          setSyncing(false)
          setSyncProgress(null)

          toast({
            title: 'Sync Failed',
            description: sync.error_message || 'An error occurred during sync',
            variant: 'destructive',
          })
        } else if (sync.sync_status === 'in_progress') {
          // Show progress during sync
          const processed = sync.items_processed || 0
          const created = sync.items_created || 0
          const updated = sync.items_updated || 0
          if (processed > 0) {
            setSyncProgress(`Processing: ${processed} items (${created} new, ${updated} updated)`)
          } else {
            setSyncProgress('Starting sync...')
          }
        } else if (attempts >= maxAttempts) {
          clearInterval(pollInterval)
          setSyncing(false)
          setSyncProgress(null)

          toast({
            title: 'Sync Timeout',
            description: 'Sync is taking longer than expected. It will continue in the background.',
          })
        }
      } catch (error) {
        console.error('Failed to poll sync status:', error)
        clearInterval(pollInterval)
        setSyncing(false)
        setSyncProgress(null)
      }
    }, 5000) // Poll every 5 seconds
  }

  const handleSettingsChange = async (
    field: keyof typeof syncSettings,
    value: boolean | number
  ) => {
    const newSettings = { ...syncSettings, [field]: value }
    setSyncSettings(newSettings)

    try {
      await apiClient.updateIntegrationSettings(newSettings)

      toast({
        title: 'Settings Updated',
        description: 'Integration settings have been updated',
      })
    } catch (error) {
      console.error('Failed to update settings:', error)
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      })

      // Revert on error
      setSyncSettings(syncSettings)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Never'
    return (toDisplayDate(dateStr) ?? new Date(dateStr)).toLocaleString()
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google Integration</CardTitle>
        <CardDescription>
          Connect your Google account to sync emails and calendar events with contacts in your CRM
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="flex items-center gap-3">
            {(integrationStatus as any)?.needs_reconnect ? (
              <>
                <AlertCircle
                  className="h-5 w-5"
                  style={{ color: CATEGORICAL.clay.solid }}
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium" style={{ color: CATEGORICAL.clay.solid }}>
                    Connection Expired
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Your Google connection has expired. Please reconnect to continue syncing.
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Previously: {integrationStatus?.integration?.google_account_email}
                  </p>
                </div>
              </>
            ) : integrationStatus?.connected ? (
              <>
                <CheckCircle
                  className="h-5 w-5"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium">Connected</p>
                  <p className="text-sm text-muted-foreground">
                    {integrationStatus.integration?.google_account_email}
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <div>
                  <p className="font-medium">Not Connected</p>
                  <p className="text-sm text-muted-foreground">
                    Connect your Google account to start syncing
                  </p>
                </div>
              </>
            )}
          </div>

          {(integrationStatus as any)?.needs_reconnect ? (
            <Button
              onClick={handleConnect}
              disabled={connecting}
              variant="outline"
              style={{
                borderColor: CATEGORICAL.clay.solid,
                color: CATEGORICAL.clay.solid,
              }}
              className="hover:opacity-80"
            >
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" />
              )}
              Reconnect
            </Button>
          ) : integrationStatus?.connected ? (
            <Button variant="destructive" size="sm" onClick={handleDisconnect} disabled={loading}>
              <Unlink className="mr-2 h-4 w-4" />
              Disconnect
            </Button>
          ) : (
            <div className="flex flex-col items-end gap-2">
              {isSuperAdmin && (
                <>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="connect-include-gmail"
                      checked={includeGmailOnConnect}
                      onCheckedChange={(c) => setIncludeGmailOnConnect(c === true)}
                    />
                    <Label htmlFor="connect-include-gmail" className="text-sm font-normal">
                      Also sync Gmail messages
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="connect-include-calendar"
                      checked={includeCalendarOnConnect}
                      onCheckedChange={(c) => setIncludeCalendarOnConnect(c === true)}
                    />
                    <Label htmlFor="connect-include-calendar" className="text-sm font-normal">
                      Also sync Calendar events
                    </Label>
                  </div>
                </>
              )}
              <Button onClick={handleConnect} disabled={connecting}>
                {connecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="mr-2 h-4 w-4" />
                )}
                Connect Google Account
              </Button>
            </div>
          )}
        </div>

        {/* Sync Settings */}
        {integrationStatus?.connected && (
          <>
            <div className="space-y-4">
              <h3 className="font-medium">Sync Settings</h3>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <Label htmlFor="sync-emails">Sync Gmail Messages</Label>
                </div>
                <Switch
                  id="sync-emails"
                  checked={syncSettings.sync_emails}
                  onCheckedChange={(checked) => handleSettingsChange('sync_emails', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <Label htmlFor="sync-calendar">Sync Calendar Events</Label>
                </div>
                <Switch
                  id="sync-calendar"
                  checked={syncSettings.sync_calendar}
                  onCheckedChange={(checked) => handleSettingsChange('sync_calendar', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="sync-frequency">Sync Frequency</Label>
                <select
                  id="sync-frequency"
                  value={syncSettings.sync_frequency_minutes}
                  onChange={(e) =>
                    handleSettingsChange('sync_frequency_minutes', parseInt(e.target.value))
                  }
                  className="px-3 py-1 border rounded-md"
                >
                  <option value="5">Every 5 minutes</option>
                  <option value="15">Every 15 minutes</option>
                  <option value="30">Every 30 minutes</option>
                  <option value="60">Every hour</option>
                </select>
              </div>
            </div>

            {/* Sync Status */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Sync Status</h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSync('incremental')}
                    disabled={syncing}
                  >
                    {syncing ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-4 w-4" />
                    )}
                    Quick Sync
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSync('full')}
                    disabled={syncing}
                  >
                    Full Sync
                  </Button>
                </div>
              </div>

              {/* Sync Progress */}
              {syncing && syncProgress && (
                <div
                  className="flex items-center gap-2 p-3 border rounded-lg"
                  style={{ backgroundColor: SEMANTIC.info.fill }}
                >
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    style={{ color: SEMANTIC.info.text }}
                    aria-hidden="true"
                  />
                  <span className="text-sm" style={{ color: SEMANTIC.info.text }}>
                    {syncProgress}
                  </span>
                </div>
              )}

              {integrationStatus.integration && (
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/50">
                  <div>
                    <p className="text-sm font-medium">Last Sync</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(integrationStatus.integration.last_sync_date)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Items Synced</p>
                    <p className="text-sm text-muted-foreground">
                      {integrationStatus.integration.stats?.emails_synced || 0} emails,{' '}
                      {integrationStatus.integration.stats?.events_synced || 0} events
                    </p>
                  </div>
                </div>
              )}

              {/* Recent Sync Logs */}
              {integrationStatus.integration?.recent_syncs &&
                integrationStatus.integration.recent_syncs.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Recent Syncs</h4>
                    <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                      {integrationStatus.integration.recent_syncs.map((log, index) => (
                        <div
                          key={log.id || index}
                          className="flex items-center justify-between py-2 border-b last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={
                                log.sync_status === 'completed'
                                  ? 'success'
                                  : log.sync_status === 'failed'
                                    ? 'destructive'
                                    : 'secondary'
                              }
                            >
                              {log.sync_status}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              {log.sync_type.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatDate(log.sync_start_date)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
